import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { useMemo } from 'react';

interface UseCommentsVisibilityProps {
  versionId?: string;
  isAttachmentField: boolean;
  isPopoverEditEnabled: boolean;
  isEditing: boolean;
}

interface UseCommentsVisibilityResult {
  isEnabled: boolean;
  shouldRenderCommentInReadView: boolean;
  shouldRenderCommentInEditView: boolean;
}

export const useCommentsVisibility = ({
  versionId,
  isAttachmentField,
  isPopoverEditEnabled,
  isEditing,
}: UseCommentsVisibilityProps): UseCommentsVisibilityResult => {
  const isCommentsPluginEnabled = isPluginEnabled(PluginKey.COMMENTS);

  /*
   * versionId: if versionId exists, we are looking at version history, so we don't render comments
   */
  const shouldRenderCommentInReadView = useMemo(
    () => isCommentsPluginEnabled && !versionId,
    [versionId, isCommentsPluginEnabled]
  );

  /*
   * versionId: if versionId exists, we are looking at version history, so we don't render comments
   * isEditing + isAttachmentField: attachment field always renders editView, and is the only time we want to render comments in editView
   * isPopoverEditEnabled: if popover edit is enabled, we don't render comments in editView for attachments
   */
  const shouldRenderCommentInEditView = useMemo(
    () =>
      isCommentsPluginEnabled &&
      !versionId &&
      !isEditing &&
      isAttachmentField &&
      !isPopoverEditEnabled,
    [
      versionId,
      isEditing,
      isAttachmentField,
      isPopoverEditEnabled,
      isCommentsPluginEnabled,
    ]
  );

  if (!isCommentsPluginEnabled) {
    return {
      isEnabled: false,
      shouldRenderCommentInReadView: false,
      shouldRenderCommentInEditView: false,
    };
  }

  return {
    isEnabled: true,
    shouldRenderCommentInReadView,
    shouldRenderCommentInEditView,
  };
};
